// src/Dashboard/pages/user/Produits/components/ProductDrawer.jsx

import React, { useEffect } from "react";
import {
  FaExternalLinkAlt,
  FaImage,
  FaLink,
  FaRocket,
  FaTimes,
} from "react-icons/fa";

import {
  ensureHttp,
  FALLBACK_PRODUCT_IMAGE,
  getProductDescription,
  getProductImage,
  getProductName,
  hostLabel,
  parseDemoLinks,
} from "../utils/productsUtils";

export default function ProductDrawer({ isOpen, product, onClose }) {
  const name = getProductName(product);
  const description = getProductDescription(product);
  const image = getProductImage(product);
  const links = parseDemoLinks(product?.lien_url_app);
  const mainLink = links[0];

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-[110]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="relative overflow-hidden bg-slate-950 px-4 py-5 text-white sm:px-6">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(76,168,47,0.25),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(1,62,142,0.45),transparent_36%)]" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/80 ring-1 ring-white/15">
                <FaRocket className="text-[#4CA82F]" />
                Application
              </p>

              <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
                {name}
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-white/80">
                {description}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
            >
              <FaTimes />
            </button>
          </div>

          {mainLink && (
            <div className="relative mt-5">
              <a
                href={ensureHttp(mainLink)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
              >
                Accéder à l’application
                <FaExternalLinkAlt className="text-xs opacity-70" />
              </a>
            </div>
          )}
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="space-y-5">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                {image ? (
                  <img
                    src={image}
                    alt={name}
                    className="max-h-[460px] w-full object-contain p-5"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK_PRODUCT_IMAGE;
                    }}
                  />
                ) : (
                  <div className="flex h-72 items-center justify-center text-slate-400">
                    <FaImage className="text-5xl" />
                  </div>
                )}
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Présentation
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
                  {description}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <FaLink />
                  Liens disponibles
                </div>

                <div className="mt-4 space-y-2">
                  {links.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucun lien disponible pour cette application.
                    </p>
                  ) : (
                    links.map((link, index) => (
                      <a
                        key={`${link}-${index}`}
                        href={ensureHttp(link)}
                        target="_blank"
                        rel="noreferrer"
                        className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-bold ring-1 transition ${
                          index === 0
                            ? "bg-[#013E8E] text-white ring-[#013E8E] hover:bg-indigo-800"
                            : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
                        }`}
                      >
                        <span className="truncate">
                          {index === 0
                            ? "Ouvrir l’application"
                            : hostLabel(link, index)}
                        </span>

                        <FaExternalLinkAlt className="shrink-0 text-xs opacity-80" />
                      </a>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-[28px] bg-slate-50 p-5 text-sm leading-6 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700">
                <p className="font-bold text-slate-950 dark:text-white">
                  Besoin d’une application similaire ?
                </p>

                <p className="mt-2">
                  Cette application fait partie des outils développés autour de
                  MonAppliClub pour répondre aux besoins concrets des clubs,
                  associations et collectivités.
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}